/**
 * The gallery's ledger of what it has sold: each placement with where title
 * now is, the covenant it carried, and whether the sale can still be unsaid.
 *
 * Pure. Placements and thumbnails go in; rows and the artist chips come out,
 * every date resolved against one `now`. The copy is `soldWorks.constants.ts`;
 * the read, the two-press withdrawal and the artist filter are
 * `useSoldWorksController.ts`. `formatRemaining` and `titleStateOf` are
 * borrowed by the settlements and transactions models, which is why they are
 * exported from here.
 *
 * Where title is is not whether the sale happened: every row is a sale, and
 * `off_registry` is the ordinary case rather than a wait. A declined or
 * withdrawn row's mark renders undisclosed rather than green, because this
 * screen knows only that this transfer did not stand and not that no
 * transfer does.
 */

import type { CovenantState, PlacedWork } from "@ondo/shared";
import { covenantState, formatMoney } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { GALLERY_MEMBERSHIP_LOST } from "./gallery.constants.ts";
import { SOLD_COPY } from "./soldWorks.constants.ts";
import { describeCodeState, formatConsignedDate } from "./gallery.model.ts";
import type { CodeState } from "./gallery.model.ts";
import type { CodeFacts } from "@ondo/shared";

/**
 * Where TITLE is, which is not whether the sale happened.
 *
 * The distinction the whole feature turns on, read at the one place a reader
 * meets it. Every row on this screen is a sale that happened; what differs is
 * whether the registry now says who owns the work.
 *
 * `off_registry` is the ordinary case rather than the exception — most art
 * buyers are not on ONDO — and it says plainly that the work is still placed
 * where it was, because there is no principal here to put it with. Calling that
 * "awaiting" would be the screen waiting for somebody who was never asked.
 */
export type TitleState = "moved" | "awaiting" | "off_registry";

export function titleStateOf(placement: PlacedWork): TitleState {
  if (placement.status === "accepted") return "moved";
  return placement.status === "unregistered" ? "off_registry" : "awaiting";
}

/** One row of the ledger, with every label already decided. */
export interface PlacedRow {
  transferId: string;
  workId: string;
  /** Opaque. It does not change when a work sells — see `SOLD_COPY.codeNote`. */
  ondoCode: string;
  title: string;
  /** 'John Currin · 1999'. Absent parts are dropped. */
  identity: string;
  /** 'sold 30 Nov 2022 → Isabelle Marchetti'. */
  sale: string;
  /** '£1,850,000'. Never empty — a sale has a price or it did not happen. */
  price: string;
  /**
   * `active` on a sale that stands, `undisclosed` on one that did not — and
   * derived rather than assumed, through the same `codeStateOf` the mark uses.
   */
  code: CodeState;
  /** 'Red · Active' / 'Not disclosed'. The verdict in words. */
  codeLabel: string;
  /**
   * What both the badge and the mark are drawn from, and it is narrow on purpose.
   *
   * `sold` is NULL on a declined or withdrawn row, deliberately. What this
   * screen knows is that THIS transfer did not stand — not that no transfer
   * does. The work went back to its owner and may have sold twice since, and
   * `ondo.gallery_placements()` tells a gallery nothing about that. So the mark
   * renders undisclosed rather than green, which is the honest reading of a
   * question this screen cannot answer.
   *
   * AND NOW THE BADGE SAYS SO TOO — OND-99. It used to answer `"green"` on those
   * rows while this very docblock explained why the mark beside it would not:
   * the badge asserted "never sold" from "this sale did not stand", which is the
   * same mistake the inventory's badge made and the one the ticket is about. One
   * `describeCodeState` call answers both now, so the badge cannot claim what
   * these facts withhold.
   */
  codeFacts: CodeFacts;
  titleState: TitleState;
  /**
   * Whether this sale can still be unsaid.
   *
   * `ondo.withdraw_transfer()` accepts `offered` and `unregistered` and refuses
   * `accepted` — once title has landed with a buyer, undoing it is a NEW
   * transfer back and not an erasure of this one. So the two open states carry
   * the control and the settled one does not, which is the same distinction
   * `titleState` already draws.
   */
  canWithdraw: boolean;
  /** What the title line says: 'Title transferred', 'Awaiting buyer'… */
  titleLabel: string;
  /** True for a sale inside `SOLD_COPY.justSoldDays`. The design's own badge. */
  justSold: boolean;
  /** The covenant panel, or null when the sale carried none. */
  covenant: { state: CovenantState; label: string; remaining: string } | null;
  artist: string;
  href: string;
  imageUrl: string | null;
}

export type SoldWorksView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; message: string; detail: string }
  | {
      kind: "ready";
      rows: PlacedRow[];
      /** 'provenance ledger · 4 works placed'. */
      summary: string;
      artists: string[];
      artist: string;
      filteredOut: boolean;
    };

/**
 * Everything the screen renders, decided here.
 *
 * `now` is a parameter rather than a call to `Date.now()` inside, so a test can
 * put a covenant on either side of its expiry without waiting a decade. Every
 * date-dependent label on this screen — the covenant state, the time remaining,
 * the JUST SOLD badge — resolves against this one clock.
 */
export function toSoldWorksView(input: {
  placements: PlacedWork[] | null;
  error: string | null;
  images: ReadonlyMap<string, string>;
  workBase: string;
  artist: string;
  now?: Date;
}): SoldWorksView {
  const { placements, error, images, workBase, artist } = input;
  const now = input.now ?? new Date();

  if (error) return { kind: "error", title: SOLD_COPY.errorTitle, message: error };
  if (!placements) return { kind: "loading", message: SOLD_COPY.loading };

  if (placements.length === 0) {
    return { kind: "empty", message: SOLD_COPY.empty, detail: SOLD_COPY.emptyDetail };
  }

  const rows = placements.map((each) => toRow(each, workBase, images, now));
  const artists = [...new Set(rows.map((row) => row.artist))].sort((a, b) =>
    a.localeCompare(b),
  );
  const shown = artist ? rows.filter((row) => row.artist === artist) : rows;

  return {
    kind: "ready",
    rows: shown,
    // Counts the LEDGER, never the filter — the rule `toInventoryView` keeps and
    // for the same reason: a total that moved when somebody picked an artist
    // would be reporting the control rather than the holding.
    summary: SOLD_COPY.placed(rows.length),
    // A control with one option is chrome pretending to be a control.
    artists: artists.length > 1 ? artists : [],
    artist,
    filteredOut: shown.length === 0,
  };
}

function toRow(
  placement: PlacedWork,
  workBase: string,
  images: ReadonlyMap<string, string>,
  now: Date,
): PlacedRow {
  const titleState = titleStateOf(placement);

  return {
    transferId: placement.transferId,
    workId: placement.workId,
    ondoCode: placement.ondoCode,
    title: placement.title,
    identity: describeIdentity(placement),
    sale: describeSale(placement),
    price: describePrice(placement),
    // ONE SET OF FACTS, and the badge and the mark are both read off it. A
    // transfer that did not stand is not a work that never sold, and neither of
    // the two things this row prints may say otherwise — see `codeFacts`.
    ...describeCodeState({
      sold:
        placement.status === "declined" || placement.status === "withdrawn"
          ? null
          : true,
      authorityAttested: null,
    }),
    titleState,
    canWithdraw: titleState !== "moved",
    titleLabel: SOLD_COPY.title[titleState],
    justSold: isJustSold(placement.soldAt, now),
    covenant: describeCovenant(placement, now),
    artist: placement.attributedName,
    href: `${workBase}/${placement.workId}`,
    imageUrl: images.get(placement.workId) ?? null,
  };
}

/** 'John Currin · 1999'. */
export function describeIdentity(placement: PlacedWork): string {
  return [placement.attributedName, placement.year]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

/**
 * 'sold 30 Nov 2022 → Isabelle Marchetti'.
 *
 * The buyer is a NAME either way — a display name off `ondo.principals` when
 * they are on the registry, and the seller's own typing when they are not. Which
 * it is is not said here: it is on the title line, where it means something.
 *
 * A buyer with no name at all is not a state any door can produce, but the row
 * survives one rather than printing a dangling arrow.
 */
export function describeSale(placement: PlacedWork): string {
  const sold = `${SOLD_COPY.soldOn} ${formatConsignedDate(placement.soldAt)}`.trim();
  return placement.buyerName ? `${sold} → ${placement.buyerName}` : sold;
}

/** '£1,850,000'. */
export function describePrice(placement: PlacedWork): string {
  return formatMoney({ minor: placement.priceMinor, currency: placement.currency });
}

/**
 * The covenant panel: what it is doing, and for how much longer.
 *
 * Null when the sale carried none, which is most sales — and an absent panel
 * rather than one reading "no covenant", because a row that announced the
 * absence of a term on every line would make the terms harder to see, not
 * easier.
 */
export function describeCovenant(
  placement: PlacedWork,
  now: Date,
): { state: CovenantState; label: string; remaining: string } | null {
  if (!placement.covenant) return null;

  const state = covenantState(placement.covenant, now);
  return {
    state,
    label: SOLD_COPY.covenant[state],
    // Only an ACTIVE term has any left. An expired one prints its end date
    // instead of a countdown, and an unlocked one prints nothing at all: how
    // long a lifted lock had to run is a fact about a term nobody is bound by.
    remaining:
      state === "active"
        ? formatRemaining(placement.covenant.endsAt, now)
        : state === "expired"
          ? `${SOLD_COPY.covenantEnded} ${formatConsignedDate(placement.covenant.endsAt)}`
          : "",
  };
}

/**
 * '5y 1m remaining'. The design's own format.
 *
 * Years and months, never days, because a covenant runs in years and a reader
 * deciding anything at this range does not care about a fortnight. Under a month
 * it says so in words rather than counting down to zero, which would read as an
 * alarm about an ordinary expiry.
 *
 * Calendar arithmetic rather than division by 365: a term set on 4 February runs
 * to 4 February, and a leap year must not make it read as a day short.
 */
export function formatRemaining(endsAt: string, now: Date): string {
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return "";

  let months =
    (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth());
  // The day of the month has not come round yet, so the last month is not whole.
  if (end.getDate() < now.getDate()) months -= 1;
  if (months <= 0) return SOLD_COPY.underAMonth;

  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts = [years > 0 ? `${years}y` : "", rest > 0 ? `${rest}m` : ""].filter(Boolean);

  return `${parts.join(" ")} ${SOLD_COPY.remaining}`;
}

/** Whether the sale is recent enough for the design's JUST SOLD badge. */
export function isJustSold(soldAt: string, now: Date): boolean {
  const sold = new Date(soldAt);
  if (Number.isNaN(sold.getTime())) return false;

  const days = (now.getTime() - sold.getTime()) / 86_400_000;
  // A sale dated in the FUTURE is not "just sold" — a gallery correcting a date
  // by a decade should not light up the newest badge on the screen.
  return days >= 0 && days <= SOLD_COPY.justSoldDays;
}

/**
 * What went wrong, in words a gallery member can act on.
 *
 * A 403 here is not a mistyped id — this route takes none. It means the caller
 * holds no gallery membership at all, which happens when a workspace URL
 * outlived the membership behind it.
 */
export function describeSoldWorksError(cause: unknown): string {
  if (cause instanceof ApiError && cause.status === 403) {
    return GALLERY_MEMBERSHIP_LOST;
  }

  return cause instanceof Error ? cause.message : SOLD_COPY.errorTitle;
}
